import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { ChevronDown, ChevronUp, User, Award } from "lucide-react";
import {
  getStudentDetailedGradebook,
  StudentDetailedGradebookResponse,
  StudentProject,
  CheckpointDetail,
  Rubric,
} from "../api/gradesApi";

interface StudentGradebookModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  selectedCourse?: string;
  selectedProject?: string;
}

function CheckpointAccordion({ checkpoint }: { checkpoint: CheckpointDetail }) {
  const [isOpen, setIsOpen] = useState(false);

  const getProgressColor = (progress: string) => {
    switch (progress.toLowerCase()) {
      case "completed":
      case "graded":
        return "bg-green-100 text-green-800";
      case "in_progress":
      case "in progress":
        return "bg-blue-100 text-blue-800";
      case "submitted":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getProgressVariant = (
    progress: string,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (progress.toLowerCase()) {
      case "completed":
      case "graded":
        return "default";
      case "in_progress":
      case "in progress":
      case "submitted":
        return "secondary";
      default:
        return "outline";
    }
  };

  const formatProgress = (progress: string) => {
    if (progress === "in_progress") return "In Progress";
    return progress.charAt(0).toUpperCase() + progress.slice(1);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg">
        <CollapsibleTrigger asChild>
          <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  )}
                  <h4 className="font-medium">{checkpoint.title}</h4>
                </div>
                <Badge
                  variant={getProgressVariant(checkpoint.progress)}
                  className={getProgressColor(checkpoint.progress)}
                >
                  {formatProgress(checkpoint.progress)}
                </Badge>
              </div>

              {checkpoint.instructor_feedback && (
                <div className="text-sm text-gray-500">Has feedback</div>
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 border-t">
            <div className="pt-4 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Attempts:</span>{" "}
                  {checkpoint.attempts || 0}
                </div>
                {/* <div>
                  <span className="font-medium">Time Spent:</span>{" "}
                  {checkpoint.time_spent_minutes || 0} minutes
                </div> */}
              </div>

              {/* Instructor Feedback */}
              {checkpoint.instructor_feedback && (
                <div>
                  <h5 className="font-medium text-sm mb-2">
                    Instructor Feedback:
                  </h5>
                  <div className="p-3 bg-blue-50 rounded-lg text-sm">
                    {checkpoint.instructor_feedback}
                  </div>
                </div>
              )}

              {/* Rubrics */}
              {checkpoint.rubrics && checkpoint.rubrics.length > 0 && (
                <div>
                  <h5 className="font-medium text-sm mb-3">
                    Rubric Breakdown:
                  </h5>
                  <div className="space-y-3">
                    {checkpoint.rubrics.map((rubric, rubricIndex) => (
                      <div key={rubricIndex} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h6 className="font-medium text-sm">
                            {rubric.rubricTitle}
                          </h6>
                          <Badge variant="outline">
                            {rubric.totalScore}/{rubric.maxScore}
                          </Badge>
                        </div>

                        <div className="space-y-2">
                          {rubric.criteriaScores.map(
                            (criterion, criterionIndex) => (
                              <div
                                key={criterionIndex}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-gray-600">
                                  {criterion.criterionName}
                                </span>
                                <div className="flex items-center space-x-2">
                                  <Progress
                                    value={
                                      (criterion.score / criterion.maxPoints) *
                                      100
                                    }
                                    className="w-16 h-2"
                                  />
                                  <span className="text-xs font-medium min-w-12 text-right">
                                    {criterion.score}/{criterion.maxPoints}
                                  </span>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function ProjectAccordion({ project }: { project: StudentProject }) {
  const [isOpen, setIsOpen] = useState(false);
  const progressPercentage =
    project.total_checkpoints > 0
      ? (project.completed_checkpoints / project.total_checkpoints) * 100
      : 0;

  const getDifficultyColor = (level: string) => {
    switch (level.toLowerCase()) {
      case "beginner":
        return "bg-green-100 text-green-800";
      case "intermediate":
        return "bg-yellow-100 text-yellow-800";
      case "advanced":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDifficulty = (level: string) => {
    return level.charAt(0).toUpperCase() + level.slice(1);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg">
        <CollapsibleTrigger asChild>
          <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  )}
                  <h4 className="font-medium">{project.project_name}</h4>
                  <Badge
                    variant="outline"
                    className={getDifficultyColor(project.difficulty_level)}
                  >
                    {formatDifficulty(project.difficulty_level)}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Course:</span>{" "}
                    {project.course}
                  </div>
                  <div>
                    <span className="font-medium">Dates:</span>{" "}
                    {project.due_date}
                  </div>
                  <div>
                    <span className="font-medium">Evaluator:</span>{" "}
                    {project.evaluator || "Not Assigned"}
                  </div>
                  <div>
                    <span className="font-medium">Score:</span>{" "}
                    {project.total_score}/{project.max_score}
                  </div>
                </div>

                <div className="mt-3">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>
                      Progress: {project.completed_checkpoints}/
                      {project.total_checkpoints} checkpoints
                    </span>
                    <span>{Math.round(progressPercentage)}%</span>
                  </div>
                  <Progress value={progressPercentage} className="h-2" />
                </div>
              </div>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 border-t">
            <div className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                <div>
                  <span className="font-medium">Enrolled:</span>{" "}
                  {formatDate(project.published_at)}
                </div>
                <div>
                  <span className="font-medium">Creator:</span>{" "}
                  {project.evaluator || "N/A"}
                </div>
                <div>
                  <span className="font-medium">Est. Hours:</span>{" "}
                  {project.estimated_hours || 0}
                </div>
                <div>
                  <span className="font-medium">Checkpoints:</span>{" "}
                  {project.completed_checkpoints}/{project.total_checkpoints}
                </div>
              </div>

              {/* Checkpoints */}
              {project.checkpoints && project.checkpoints.length > 0 && (
                <div>
                  <h5 className="font-medium text-sm mb-3">
                    Checkpoint Details:
                  </h5>
                  <div className="space-y-2">
                    {project.checkpoints.map((checkpoint) => (
                      <CheckpointAccordion
                        key={checkpoint.checkpoint_id}
                        checkpoint={checkpoint}
                      />
                    ))}
                  </div>
                </div>
              )}

              {(!project.checkpoints || project.checkpoints.length === 0) && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No checkpoints available for this project
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export default function StudentGradebookModal({
  open,
  onOpenChange,
  userId,
  userName,
  selectedCourse,
  selectedProject,
}: StudentGradebookModalProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Fetch student detailed gradebook data with filters
  const {
    data: studentData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "studentDetailedGradebook",
      userId,
      currentPage,
      selectedCourse,
      selectedProject,
    ],
    queryFn: () => {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (selectedCourse && selectedCourse !== "all")
        params.course = selectedCourse;
      if (selectedProject && selectedProject !== "all")
        params.project = selectedProject;
      return getStudentDetailedGradebook(userId, params);
    },
    enabled: open && !!userId,
  });

  // Debug: Log the API response
  React.useEffect(() => {
    if (studentData) {
    }
  }, [studentData]);

  const totalPages = studentData?.data?.pagination?.totalPages || 1;
  const totalProjects = studentData?.data?.pagination?.totalItems || 0;
  const projects = studentData?.data?.data?.projects || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] bg-white border flex flex-col">
        <DialogHeader className="flex-shrink-0 border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Student Profile - {userName}</span>
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8 flex-1">
            <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            <span className="ml-2 text-sm text-gray-600">
              Loading student data...
            </span>
          </div>
        )}

        {error && (
          <div className="text-center py-8 flex-1">
            <p className="text-red-600">Failed to load student data</p>
            <p className="text-sm text-gray-500 mt-1">
              {error instanceof Error ? error.message : "An error occurred"}
            </p>
          </div>
        )}

        {!isLoading && !error && studentData?.data && (
          <>
            {/* Fixed User Info Section */}
            <div className="flex-shrink-0 p-4 bg-gray-50 border-b">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">
                    {studentData.data.data.user.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {studentData.data.data.user.email}
                  </p>
                  <div className="flex items-center space-x-4 mt-2">
                    <Badge variant="outline">Student</Badge>
                    <Badge
                      variant={
                        studentData.data.data.user.status === "active"
                          ? "default"
                          : "secondary"
                      }
                      className={
                        studentData.data.data.user.status === "active"
                          ? "bg-green-100 text-green-800"
                          : ""
                      }
                    >
                      {studentData.data.data.user.status}
                    </Badge>
                    {totalProjects > 0 && (
                      <Badge variant="outline" className="bg-blue-50">
                        {totalProjects} Project{totalProjects !== 1 ? "s" : ""}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable Projects Section */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center space-x-2 p-4 pb-2">
                <Award className="h-5 w-5" />
                <h3 className="font-semibold text-lg">
                  Projects{" "}
                  {studentData.data.data.projects.length > 0 &&
                    `(${studentData.data.data.projects.length})`}
                </h3>
              </div>

              <div className="flex-1 px-4 pb-4 overflow-y-auto">
                {studentData.data.data.projects.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {selectedCourse !== "all" || selectedProject !== "all"
                      ? "No projects found matching the selected filters"
                      : "No projects assigned to this student"}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {studentData.data.data.projects.map((project) => (
                      <ProjectAccordion
                        key={project.project_id}
                        project={project}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center pt-4 border-t flex-shrink-0">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
